import { Card, CardContent, Typography, useTheme } from '@mui/joy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ITssDashboardFilters, VerticalBarChart } from 'reports/components';
import { CHART_COLORS } from 'reports/constants';
import { useGetBomHeader } from 'reports/hooks';
import { IBomHeaderJob } from 'reports/types';
import { Level, ModeType, Variant } from 'theme/enums';

const QUARTERS_TO_DISPLAY = 5;
const MONTHS_PER_QUARTER = 3;
const QUARTERS_PER_YEAR = 4;

interface IQuarter {
  label: string;
  startMonth: number;
  endMonth: number;
  year: number;
}

interface IProps {
  title: string;
  startDate: string;
  endDate: string;
  filters?: ITssDashboardFilters;
  height: number | string;
}

export const TrendingCustomersChart = ({ title, startDate, endDate, filters, height }: IProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === ModeType.DARK;
  const previousYearColor = isDarkMode
    ? CHART_COLORS.previousYear.dark
    : CHART_COLORS.previousYear.light;

  const requestPayload = useMemo(
    () => ({
      filters: {
        ...filters,
        jobCreatedAtSince: startDate,
        jobCreatedAtUntil: endDate,
      },
    }),
    [startDate, endDate, filters]
  );

  const { records, isLoading, isFetching } = useGetBomHeader(requestPayload);

  const isDataLoading = isLoading || isFetching;

  const transformedData = useMemo(() => {
    if (!records.length) {
      return { datasets: [], labels: [] };
    }

    const quarters = getQuarters();

    const quarterData: Array<{ boms: number; customers: Set<string> }> = quarters.map(() => ({
      boms: 0,
      customers: new Set<string>(),
    }));

    records.forEach((job: IBomHeaderJob) => {
      const jobDate = new Date(job.jobCreatedAt);
      const jobMonth = jobDate.getMonth();
      const jobYear = jobDate.getFullYear();

      const quarterIndex = quarters.findIndex(
        (quarter) =>
          quarter.year === jobYear && jobMonth >= quarter.startMonth && jobMonth <= quarter.endMonth
      );

      if (quarterIndex !== -1) {
        quarterData[quarterIndex].boms++;

        const customerIdentifier = job.customerCompanyName;
        if (customerIdentifier) {
          quarterData[quarterIndex].customers.add(customerIdentifier);
        }
      }
    });

    const bomsData = quarterData.map((q) => q.boms);
    const customersData = quarterData.map((q) => q.customers.size);

    return {
      datasets: [
        {
          backgroundColor: previousYearColor,
          data: bomsData,
          label: t('labelCases'),
        },
        {
          backgroundColor: CHART_COLORS.currentYear,
          data: customersData,
          label: t('labelDistinctCustomers'),
        },
      ],
      labels: quarters.map((q) => q.label),
    };
  }, [previousYearColor, records, t]);

  return (
    <Card variant={Variant.OUTLINED}>
      <CardContent>
        <Typography level={Level.TITLE_MD} sx={{ marginBottom: 2 }}>
          {title}
        </Typography>
        <VerticalBarChart data={transformedData} isLoading={isDataLoading} height={height} />
      </CardContent>
    </Card>
  );
};

const getQuarters = (): IQuarter[] => {
  const quarters: IQuarter[] = [];

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.floor(now.getMonth() / MONTHS_PER_QUARTER);

  for (let i = QUARTERS_TO_DISPLAY - 1; i >= 0; i--) {
    const quarterOffset = currentQuarter - i;
    const year = currentYear + Math.floor(quarterOffset / QUARTERS_PER_YEAR);
    const quarter = ((quarterOffset % QUARTERS_PER_YEAR) + QUARTERS_PER_YEAR) % QUARTERS_PER_YEAR;
    const startMonth = quarter * MONTHS_PER_QUARTER;
    const endMonth = startMonth + (MONTHS_PER_QUARTER - 1);

    quarters.push({
      endMonth,
      label: `${year} Q${quarter + 1}`,
      startMonth,
      year,
    });
  }

  return quarters;
};
